import type { OwnedSkin, SkinMutationResponse, SkinView } from '@kenocyber/shared';
import { useCallback, useRef, useState } from 'react';
import { equipSkin, fetchSkins, HuntApiError, unequipSkin } from '../api/hunt';

/**
 * The wardrobe's state machine (gate V23.1).
 *
 * ## The server owns the answer
 *
 * Every mutation replaces the whole local view with the response it got back.
 * There is no optimistic state that survives: an equip that races another tab
 * loses, and the loser has to end up drawing what the server actually did
 * rather than what this tab hoped for. `SkinMutationResponse` carries the same
 * shape as the read for exactly this reason, so a change is a whole new view.
 *
 * ## One idempotency key per INTENTION
 *
 * The key is minted when the player asks for something and reused for as long
 * as that same request is being retried. A key that changed on every attempt
 * would turn a retried equip into a second equip; a key that never changed
 * would make a genuinely new intention look like a replay of the old one. It is
 * cleared once the request settles, so the next click is a new intention.
 *
 * ## A version conflict is not an error to show and forget
 *
 * Another tab moved the slot first. The right response is to take the newest
 * authoritative state - which the refusal does not carry - so the conflict path
 * re-reads and tells the player what happened, rather than leaving the screen
 * showing a version the server has already left behind.
 */

export interface SkinWardrobeState {
  readonly catalogue: readonly SkinView[];
  readonly owned: readonly OwnedSkin[];
  readonly loadoutVersion: number;
  readonly equippedInstanceId: string | null;
  readonly loading: boolean;
  readonly busy: boolean;
  readonly pendingInstanceId: string | null;
  readonly error: string | null;
}

const EMPTY: SkinWardrobeState = {
  catalogue: [],
  owned: [],
  loadoutVersion: 0,
  equippedInstanceId: null,
  loading: false,
  busy: false,
  pendingInstanceId: null,
  error: null,
};

/** A refusal, in the player's language. Never a status code on screen. */
function messageFor(error: unknown): string {
  if (error instanceof HuntApiError) {
    if (error.status === 409) {
      return 'Sua lista estava desatualizada — outra aba mudou o equipamento. Atualizamos e você pode tentar de novo.';
    }
    if (error.status === 404) {
      return 'Essa cópia não está mais disponível nesta conta.';
    }
    if (error.status === 401 || error.status === 403) {
      return 'Sua sessão expirou. Entre novamente para mudar a skin.';
    }
  }
  return 'Não foi possível mudar a skin agora. Tente de novo em instantes.';
}

export interface SkinWardrobeOptions {
  /**
   * Called with the WHOLE confirmed answer, after a 200 the schema validated.
   *
   * Deliberately the response and not the instance id that was clicked. The
   * id is what this tab hoped for; the answer is what the server did, and it
   * carries the run that was reconfigured with it. A callback given only the
   * id would leave its listener to go and ask what happened - which is the
   * second bootstrap this gate exists to remove.
   *
   * Never called on a refusal, a conflict or a transport failure: there is no
   * confirmed state to hand anybody, and the arena must keep drawing what it
   * is drawing.
   */
  readonly onConfirmed?: (response: SkinMutationResponse) => void;
}

export function useSkinWardrobe(options: SkinWardrobeOptions = {}) {
  const [state, setState] = useState<SkinWardrobeState>(EMPTY);
  /**
   * The callback, kept in a ref and refreshed every render.
   *
   * A caller writing `onConfirmed={(r) => hunt.adoptSkinChange(r.hunt)}` mints
   * a new function each render. Putting it in the `mutate` dependency list
   * would rebuild `equip` and `unequip` on every one of them, and the buttons
   * would lose the identity a double-click guard is built on.
   */
  const confirmed = useRef(options.onConfirmed);
  confirmed.current = options.onConfirmed;
  /**
   * The key for the request currently being attempted.
   *
   * A ref rather than state: it must not cause a render, and it must be read
   * synchronously by the click that follows a failed attempt.
   */
  const key = useRef<string | null>(null);
  /** Guards a double click even before React has re-rendered the button. */
  const inFlight = useRef(false);

  const apply = useCallback(
    (response: {
      skins: readonly SkinView[];
      owned: readonly OwnedSkin[];
      loadoutVersion: number;
      equippedSkinInstanceId: string | null;
    }) => {
      setState((previous) => ({
        ...previous,
        catalogue: response.skins,
        // The server's order, kept. Re-sorting here would be a second ordering
        // rule that could disagree with the one the contract promises.
        owned: response.owned,
        loadoutVersion: response.loadoutVersion,
        equippedInstanceId: response.equippedSkinInstanceId,
        loading: false,
        busy: false,
        pendingInstanceId: null,
        error: null,
      }));
    },
    [],
  );

  const refresh = useCallback(async () => {
    setState((previous) => ({ ...previous, loading: true, error: null }));
    try {
      apply(await fetchSkins());
    } catch (error) {
      setState((previous) => ({
        ...previous,
        loading: false,
        busy: false,
        pendingInstanceId: null,
        error: messageFor(error),
      }));
    }
  }, [apply]);

  const mutate = useCallback(
    async (
      instanceId: string | null,
      run: (input: {
        expectedLoadoutVersion: number;
        idempotencyKey: string;
      }) => Promise<SkinMutationResponse>,
    ) => {
      if (inFlight.current) {
        return;
      }
      inFlight.current = true;
      key.current ??= `skin:${crypto.randomUUID()}`;
      setState((previous) => ({
        ...previous,
        busy: true,
        pendingInstanceId: instanceId,
        error: null,
      }));
      try {
        const response = await run({
          expectedLoadoutVersion: state.loadoutVersion,
          idempotencyKey: key.current,
        });
        // Settled: the next click is a new intention and gets a new key.
        key.current = null;
        apply(response);
        /*
         * And only now, with the server's own answer in hand.
         *
         * After `apply`, so the shelf and the arena can never be told two
         * different things in two different orders; and inside the `try`'s
         * success path, so a refusal, a 409 or a dropped connection reaches
         * nobody. Whatever this hands over was confirmed, parsed and stored.
         */
        confirmed.current?.(response);
      } catch (error) {
        setState((previous) => ({
          ...previous,
          busy: false,
          pendingInstanceId: null,
          error: messageFor(error),
        }));
        /*
         * A stale version means somebody else won. Take the newest state, and
         * let the player retry as a NEW intention - the old key described a
         * change against a version that no longer exists.
         */
        if (error instanceof HuntApiError && error.status === 409) {
          key.current = null;
          await refresh();
          setState((previous) => ({ ...previous, error: messageFor(error) }));
        }
      } finally {
        inFlight.current = false;
      }
    },
    [apply, refresh, state.loadoutVersion],
  );

  const equip = useCallback(
    (itemInstanceId: string) =>
      mutate(itemInstanceId, (input) => equipSkin({ itemInstanceId, ...input })),
    [mutate],
  );

  const unequip = useCallback(
    () => mutate(state.equippedInstanceId, (input) => unequipSkin(input)),
    [mutate, state.equippedInstanceId],
  );

  /** Everything this account knew, dropped. For a logout or an account swap. */
  const reset = useCallback(() => {
    key.current = null;
    inFlight.current = false;
    setState(EMPTY);
  }, []);

  return { state, refresh, equip, unequip, reset };
}
